import { storeConfig } from "@/config/storeConfig";
import { normalizePhone } from "./commerce";
import type {
  CartItem,
  CheckoutForm,
  CustomerRecord,
  CustomerStatus,
  OrderItemRecord,
  OrderRecord,
} from "@/types";
import { ORDER_STATUS } from "@/types";

const CUSTOMERS_KEY = "aurea-clientes";
const ORDERS_KEY = "aurea-pedidos";
const COUNTERS_KEY = "aurea-contadores";

interface Counters {
  customer: number;
  order: number;
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* armazenamento indisponível */
  }
}

const pad = (value: number, size: number): string => String(value).padStart(size, "0");

function readCounters(): Counters {
  return readJSON<Counters>(COUNTERS_KEY, { customer: 0, order: 0 });
}

function writeCounters(counters: Counters): void {
  writeJSON(COUNTERS_KEY, counters);
}

export const generateCustomerCode = (): string => {
  const counters = readCounters();
  counters.customer += 1;
  writeCounters(counters);
  return `AUR-CLI-${pad(counters.customer, 5)}`;
};

export const generateOrderCode = (): string => {
  const counters = readCounters();
  counters.order += 1;
  writeCounters(counters);
  return `AUR-PED-${pad(counters.order, 5)}`;
};

/* --------------------------- Clientes ------------------------------ */

export const listCustomers = (): CustomerRecord[] => readJSON<CustomerRecord[]>(CUSTOMERS_KEY, []);

/** Atualiza dados locais de uma ficha de cliente (notas, status, etc.). */
export function updateCustomer(code: string, patch: Partial<CustomerRecord>): void {
  const customers = listCustomers().map((customer) =>
    customer.code === code ? { ...customer, ...patch } : customer,
  );
  saveCustomers(customers);
}

export const findCustomerByPhone = (phone: string): CustomerRecord | undefined => {
  const normalized = normalizePhone(phone);
  return listCustomers().find((customer) => customer.phoneNormalized === normalized);
};

const saveCustomers = (customers: CustomerRecord[]): void =>
  writeJSON(CUSTOMERS_KEY, customers);

export function upsertCustomer(
  form: CheckoutForm,
  orderTotal: number,
  nowIso: string,
): { customer: CustomerRecord; created: boolean } {
  const normalized = normalizePhone(form.phone);
  const email = form.email.trim().toLowerCase();
  const customers = listCustomers();
  const existing = customers.find(
    (customer) =>
      customer.phoneNormalized === normalized ||
      (email !== "" && customer.email.trim().toLowerCase() === email),
  );

  if (existing) {
    const orderCount = existing.orderCount + 1;
    const updated: CustomerRecord = {
      ...existing,
      name: form.name.trim(),
      phone: form.phone,
      email: form.email.trim() || existing.email,
      address: { ...existing.address, ...form.address },
      preferredDelivery: form.deliveryMethod || existing.preferredDelivery,
      lastOrderAt: nowIso,
      orderCount,
      totalSpent: Number((existing.totalSpent + orderTotal).toFixed(2)),
      status: "recorrente" as CustomerStatus,
      notes: form.notes.trim() || existing.notes,
      consent: form.consent,
    };
    saveCustomers(customers.map((item) => (item.code === existing.code ? updated : item)));
    return { customer: updated, created: false };
  }

  const created: CustomerRecord = {
    code: generateCustomerCode(),
    name: form.name.trim(),
    phone: form.phone,
    phoneNormalized: normalized,
    email: form.email.trim(),
    address: { ...form.address },
    preferredDelivery: form.deliveryMethod,
    firstOrderAt: nowIso,
    lastOrderAt: nowIso,
    orderCount: 1,
    totalSpent: Number(orderTotal.toFixed(2)),
    origin: storeConfig.origin,
    consent: form.consent,
    notes: form.notes.trim(),
    status: "novo",
  };
  saveCustomers([...customers, created]);
  return { customer: created, created: true };
}

/* ---------------------------- Pedidos ------------------------------ */

export const listOrders = (): OrderRecord[] => readJSON<OrderRecord[]>(ORDERS_KEY, []);

export function createOrder(params: {
  customer: CustomerRecord;
  items: CartItem[];
  subtotal: number;
  discount: number;
  shippingFee: number | null;
  total: number;
  deliveryMethodId: string;
  deliveryLabel: string;
  deliveryDeadline: string;
  freeShipping: boolean;
  address: CheckoutForm["address"] | null;
  bus: CheckoutForm["bus"] | null;
  notes: string;
  createdAt: string;
}): OrderRecord {
  const date = new Date(params.createdAt);
  const orderItems: OrderItemRecord[] = params.items.map((item) => ({
    productId: item.productId,
    code: item.code,
    name: item.name,
    category: item.category,
    colorName: item.colorName,
    size: item.size,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    subtotal: Number((item.unitPrice * item.quantity).toFixed(2)),
    image: item.image,
  }));

  const order: OrderRecord = {
    code: generateOrderCode(),
    customerCode: params.customer.code,
    createdAt: params.createdAt,
    date: date.toLocaleDateString("pt-BR"),
    time: date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    items: orderItems,
    itemCount: orderItems.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: Number(params.subtotal.toFixed(2)),
    discount: Number(params.discount.toFixed(2)),
    shippingFee: params.shippingFee === null ? null : Number(params.shippingFee.toFixed(2)),
    total: Number(params.total.toFixed(2)),
    deliveryMethodId: params.deliveryMethodId,
    deliveryLabel: params.deliveryLabel,
    deliveryDeadline: params.deliveryDeadline,
    freeShipping: params.freeShipping,
    address: params.address,
    bus: params.bus,
    notes: params.notes,
    origin: storeConfig.origin,
    status: ORDER_STATUS.started,
  };

  writeJSON(ORDERS_KEY, [...listOrders(), order]);
  return order;
}

/** Atualiza o status de um pedido salvo neste navegador, registrando o histórico. */
export function updateOrderStatus(code: string, status: string): void {
  const orders = listOrders().map((order) =>
    order.code === code
      ? {
          ...order,
          status,
          statusHistory: [
            ...(order.statusHistory ?? [{ status: order.status, at: order.createdAt }]),
            { status, at: new Date().toISOString() },
          ],
        }
      : order,
  );
  writeJSON(ORDERS_KEY, orders);
}

/** Restaura os dados demonstrativos locais (usado pelo painel). */
export const restoreDemoData = (): void => {
  try {
    localStorage.removeItem(CUSTOMERS_KEY);
    localStorage.removeItem(ORDERS_KEY);
    localStorage.removeItem(COUNTERS_KEY);
  } catch {
    /* armazenamento indisponível */
  }
};